/**
 * Profile — личный кабинет: шапка (аватар, контакты, статусы KYC/ШЭРП, «всего куплено»),
 * баннеры (подписание оферты/ШЭРП), чат поддержки, список заявок, покупка.
 * Данные — из нашего бэка; баннеры брокера/ШЭРП появляются только при активных интеграциях.
 */
import { useEffect, useRef, useState, type ReactNode, type TouchEvent } from "react";
import { Badge } from "@/components/Badge";
import { ActionRow } from "@/components/ActionRow";
import type { Profile as ProfileT } from "@/lib/storage";
import { clearAll } from "@/lib/storage";
import { brokerApi, sherpApi, supportApi, type Account, type ApiOrder, type ApiWithdrawal, type IssuedCard, type OrderStatusApi, type UsdtAsset, type WithdrawalMethod, type WithdrawalStatusApi } from "@/lib/api";
import { AccountFlip } from "@/screens/cards/AccountFlip";
import {
  formatRelative,
  formatRub,
  formatUsdt,
  initialsFromName,
  maskMiddle,
  maskPhone,
} from "@/lib/format";

export type KycState = "approved" | "pending" | "rejected" | "none";

type Props = {
  profile: ProfileT;
  orders: ApiOrder[];
  account: Account;
  usdt?: UsdtAsset | null;
  rate?: number | null;
  withdrawEnabled?: boolean;
  withdrawals?: ApiWithdrawal[];
  onBuy: () => void;
  onTopUpUsdt?: () => void;
  onWithdraw?: () => void;
  onWithdrawUsdt?: () => void;
  onCards?: () => void;
  // выпущенная карта: панель счёта переворачивается на неё, кнопки под ней — карточные
  card?: IssuedCard | null;
  onOpenCard?: () => void;
  onCardTopUp?: () => void;
  onCardWithdraw?: () => void;
  onOpenWithdrawal?: (w: ApiWithdrawal) => void;
  onOpenOrder: (id: string) => void;
  onHideOrder?: (id: string) => void;
  onUnhideOrder?: (id: string) => void;
  onSupport: () => void;
  // видимость кнопок главного экрана (из промокода клиента); по умолчанию — показывать
  showSupport?: boolean;
  onAgreement: () => void;
  onSherp: () => void;
  onReset: () => void;
  resetLabel?: string;
  kycState?: KycState;
  onVerify?: () => void;
};

const STATUS_LABEL: Record<OrderStatusApi, { label: string; tone: "ok" | "warn" | "muted" | "err" }> = {
  awaiting_kyc: { label: "Проверка верификации", tone: "muted" },
  awaiting_payment: { label: "Ждём оплату", tone: "warn" },
  processing: { label: "Оплата получена", tone: "warn" },
  paid: { label: "Оплачена", tone: "ok" },
  sending: { label: "Обработка", tone: "warn" },
  completed: { label: "Выполнена", tone: "ok" },
  send_failed: { label: "Обработка", tone: "warn" },
  rejected: { label: "Отклонена", tone: "err" },
  cancelled: { label: "Отменена", tone: "muted" },
  expired: { label: "Просрочена", tone: "muted" },
};

const WITHDRAWAL_STATUS: Record<WithdrawalStatusApi, { label: string; tone: "ok" | "warn" | "muted" | "err" }> = {
  pending: { label: "В обработке", tone: "warn" },
  processing: { label: "Отправляем", tone: "warn" },
  completed: { label: "Выполнен", tone: "ok" },
  rejected: { label: "Отклонён", tone: "err" },
  cancelled: { label: "Отменён", tone: "muted" },
};

const WD_METHOD_LABEL: Record<WithdrawalMethod, string> = {
  card: "Зарубежная карта",
  swift: "SWIFT",
  crypto: "Крипто-кошелёк",
};

function wdDestSummary(w: ApiWithdrawal): string {
  const d = w.destination || {};
  if (w.method === "card") return `•••• ${(d.card_number || "").slice(-4)}`;
  if (w.method === "swift") return d.bank_name || "SWIFT";
  return maskMiddle(d.wallet_address || "", 4, 4);
}

const GLASS = "border border-white/[0.08] bg-white/[0.04] backdrop-blur-xl";

const TERMINAL: OrderStatusApi[] = ["completed", "cancelled", "rejected"];

export function Profile({ profile, orders, account, usdt = null, rate = null, withdrawEnabled = false, withdrawals = [], onBuy, onTopUpUsdt, onWithdraw, onWithdrawUsdt, onCards, card = null, onOpenCard, onCardTopUp, onCardWithdraw, onOpenWithdrawal, onOpenOrder, onHideOrder, onUnhideOrder, onSupport, showSupport = true, onAgreement, onSherp, onReset, resetLabel, kycState, onVerify }: Props) {
  const kyc: KycState = kycState ?? (profile.kyc ? "approved" : "none");
  // листы выбора актива: пополнение (RUB — флоу заявки, USDT — QR) и вывод (RUB — счёт, USDT — он-чейн)
  const [topupOpen, setTopupOpen] = useState(false);
  const [withdrawOpen, setWithdrawOpen] = useState(false);
  // какой стороной повёрнута панель: счёт или выпущенная карта
  const [showCard, setShowCard] = useState(false);

  const addressText = profile.address
    ? profile.address.value || [profile.address.city, profile.address.address_line].filter(Boolean).join(", ")
    : undefined;
  // контакты только для просмотра (редактирование убрано — задаются один раз в онбординге)
  const [detailsOpen, setDetailsOpen] = useState(false);

  const [supportUnread, setSupportUnread] = useState(0);
  // оферта у брокера: pending — показываем баннер для подписания; signed — пункт «посмотреть ДБО»
  const [agreementPending, setAgreementPending] = useState(false);
  const [agreementSigned, setAgreementSigned] = useState(false);
  useEffect(() => {
    brokerApi
      .status()
      .then((s) => {
        setAgreementPending(s.active && s.onboarded && s.signing_enabled !== false && !s.agreement_signed);
        setAgreementSigned(Boolean(s.active && s.onboarded && s.agreement_signed));
      })
      .catch(() => undefined);
  }, []);
  // статус Договора присоединения ШЭРП: off (ось выключена) | pending | signed
  const [sherpState, setSherpState] = useState<"off" | "pending" | "signed">("off");
  useEffect(() => {
    sherpApi
      .status()
      .then((s) => setSherpState(!s.enabled ? "off" : s.signed ? "signed" : "pending"))
      .catch(() => undefined);
  }, []);
  useEffect(() => {
    supportApi
      .unread()
      .then((r) => setSupportUnread(r.count))
      .catch(() => undefined);
  }, []);

  // скрытые уезжают в «Историю»; из видимых — терминальные сверху (юзер увидит финал и уберёт),
  // активные ниже
  const [histOpen, setHistOpen] = useState(false);
  const visible = orders.filter((o) => !o.hidden);
  const hiddenOrders = orders.filter((o) => o.hidden);
  const sortedVisible = [
    ...visible.filter((o) => TERMINAL.includes(o.status)),
    ...visible.filter((o) => !TERMINAL.includes(o.status)),
  ];

  return (
    <div className="relative flex min-h-dvh flex-col bg-surface-base px-5 pt-8 pb-[calc(env(safe-area-inset-bottom)+1.25rem)] text-white">
      {/* Фон профиля. Слои снизу вверх (pointer-events-none — не мешают тапам):
          1) фолбэк — изумрудное CSS-свечение (виден, только если картинка не загрузилась);
          2) сгенерированный фон public/profile-bg.jpg (свечение уже запечено в него).
          fixed+cover — держится по вьюпорту и не растягивается на длинной странице. */}
      <div className="pointer-events-none fixed inset-0">
        <div className="absolute left-1/2 top-0 h-64 w-[85%] -translate-x-1/2 rounded-full bg-brand/20 blur-[90px]" />
        <div
          className="absolute inset-0 bg-cover bg-top bg-no-repeat"
          style={{ backgroundImage: "url(/profile-bg.jpg)" }}
        />
      </div>

      <div className="relative flex-1">

        {/* Идентити — тап по имени раскрывает контакты (email/адрес) */}
        <div className="flex items-start gap-3">
          <button
            onClick={() => setDetailsOpen((v) => !v)}
            className="flex min-w-0 flex-1 items-start gap-3 text-left active:opacity-80"
          >
            <div className="grid h-14 w-14 shrink-0 place-items-center rounded-2xl bg-gradient-to-br from-brand-400 to-brand-600 text-[18px] font-semibold text-surface-base shadow-lg shadow-brand/25">
              {initialsFromName(profile.display_name ?? profile.passport?.full_name ?? "?")}
            </div>
            <div className="min-w-0 flex-1 pt-0.5">
              <div className="flex items-center gap-1.5">
                <span className="truncate text-[17px] font-semibold">
                  {profile.display_name ?? profile.passport?.full_name ?? "Клиент"}
                </span>
                <svg
                  viewBox="0 0 20 20"
                  className={`h-4 w-4 shrink-0 text-white/40 transition-transform ${detailsOpen ? "rotate-180" : ""}`}
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="1.7"
                >
                  <path d="M6 8l4 4 4-4" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              </div>
              <div className="text-[13px] text-white/45">{maskPhone(profile.phone)}</div>
            </div>
          </button>
          <div className="flex flex-col items-end gap-1.5">
            {kyc === "approved" && <Badge tone="ok" onClick={onVerify}>Верификация ✓</Badge>}
            {kyc === "pending" && <Badge tone="warn" onClick={onVerify}>Верификация на проверке</Badge>}
            {kyc === "rejected" && <Badge tone="err" onClick={onVerify}>Верификация отклонена ›</Badge>}
            {kyc === "none" && <Badge tone="muted" onClick={onVerify}>Верификация не пройдена ›</Badge>}
            {rate != null && (
              <div className="pr-1 text-[11px] font-medium tabular-nums text-white/55">
                1 USD = {rate.toFixed(2)} ₽
              </div>
            )}
            {sherpState === "signed" && <Badge tone="ok" onClick={onSherp}>Заявление ✓</Badge>}
            {sherpState === "pending" && <Badge tone="warn" onClick={onSherp}>Заявление ›</Badge>}
          </div>
        </div>

        {/* Контакты — раскрываются по стрелке у имени, только просмотр (редактирование убрано) */}
        {detailsOpen && (
          <div className={`mt-3 overflow-hidden rounded-2xl ${GLASS}`}>
            <ContactRow icon="✉️" label="Email" value={profile.email} />
            <div className="mx-4 h-px bg-white/[0.06]" />
            <ContactRow icon="📍" label="Адрес" value={addressText} />
          </div>
        )}

        {/* Счёт (активы RUB + USDT) + действия. «Вывести» (RUB на карту/SWIFT) доступен всем;
            вывод на TRC20-кошелёк и USDT-операции — только с читом hesoyam (модалка выбора). */}
        <div className="mt-5">
          <AccountFlip
            account={account}
            usdt={usdt}
            rate={rate}
            card={card}
            showCard={showCard && card != null}
            onToggle={() => setShowCard((v) => !v)}
            onOpenCard={() => onOpenCard?.()}
          />
          {/* кнопки принадлежат видимой стороне: у карты свои пополнение и вывод */}
          <div className="mt-3 grid grid-cols-2 gap-2.5">
            {showCard && card ? (
              <>
                <button
                  onClick={() => onCardTopUp?.()}
                  className="flex items-center justify-center gap-1.5 rounded-2xl bg-brand px-4 py-3.5 text-[15px] font-semibold text-surface-base shadow-lg shadow-brand/25 active:brightness-95"
                >
                  <span className="text-[17px] leading-none">↓</span> Пополнить
                </button>
                <button
                  onClick={() => onCardWithdraw?.()}
                  className={`flex items-center justify-center gap-1.5 rounded-2xl px-4 py-3.5 text-[15px] font-semibold active:opacity-80 ${GLASS} text-white`}
                >
                  <span className="text-[17px] leading-none">↑</span> Вывести
                </button>
              </>
            ) : (
              <>
                <button
                  onClick={() => (withdrawEnabled && onTopUpUsdt ? setTopupOpen(true) : onBuy())}
                  className="flex items-center justify-center gap-1.5 rounded-2xl bg-brand px-4 py-3.5 text-[15px] font-semibold text-surface-base shadow-lg shadow-brand/25 active:brightness-95"
                >
                  <span className="text-[17px] leading-none">↓</span> Пополнить
                </button>
                <button
                  onClick={() => (withdrawEnabled && onWithdrawUsdt ? setWithdrawOpen(true) : onWithdraw?.())}
                  className={`flex items-center justify-center gap-1.5 rounded-2xl px-4 py-3.5 text-[15px] font-semibold active:opacity-80 ${GLASS} text-white`}
                >
                  <span className="text-[17px] leading-none">↑</span> Вывести
                </button>
              </>
            )}
          </div>
        </div>

        {/* Баннеры + поддержка */}
        <div className="mt-4 space-y-3">
          {agreementPending && (
            <ActionRow
              tone="warn"
              icon="📝"
              iconClass="bg-amber-400/20"
              title="Подпишите договор-оферту"
              subtitle="Нужно для покупки — займёт минуту"
              onClick={onAgreement}
            />
          )}

          {agreementSigned && (
            <ActionRow
              surface="glass"
              icon="📄"
              iconClass="bg-emerald-400/15"
              title="Договор с брокером (ДБО)"
              subtitle="Подписан — посмотреть документ"
              onClick={onAgreement}
            />
          )}

          {sherpState === "pending" && (
            <ActionRow
              tone="warn"
              icon="🏦"
              iconClass="bg-amber-400/20"
              title="Подпишите Заявление о присоединении"
              subtitle="Договор с платёжным оператором ЗАО «ШЭРП»"
              onClick={onSherp}
            />
          )}

          {sherpState === "signed" && (
            <ActionRow
              surface="glass"
              icon="🏦"
              iconClass="bg-emerald-400/15"
              title="Заявление о присоединении"
              subtitle="Подписано — посмотреть документ"
              onClick={onSherp}
            />
          )}

          {onCards && (
            <ActionRow
              surface="glass"
              icon="💳"
              iconClass="bg-brand/15"
              title="Карты"
              subtitle={card ? `Карта •• ${card.last4} — операции и история` : "Выпустить карту для оплаты"}
              onClick={card && onOpenCard ? onOpenCard : onCards}
            />
          )}

          {showSupport && (
            <ActionRow
              surface="glass"
              icon="💬"
              iconClass="bg-brand/15"
              title="Чат с поддержкой"
              subtitle="Задайте вопрос — ответим здесь"
              onClick={onSupport}
              trailing={
                <span className="flex shrink-0 items-center gap-2">
                  {supportUnread > 0 && (
                    <span className="grid h-5 min-w-5 place-items-center rounded-full bg-red-500 px-1.5 text-[12px] font-semibold text-white">
                      {supportUnread}
                    </span>
                  )}
                  <span className="text-white/30">›</span>
                </span>
              }
            />
          )}
        </div>

        {/* Заявки */}
        <div className="mb-3 mt-6 flex items-center justify-between">
          <h2 className="text-[17px] font-semibold">Пополнения</h2>
          <span className="text-[13px] text-white/45">{visible.length}</span>
        </div>

        {orders.length === 0 ? (
          <div className={`rounded-3xl px-4 py-10 text-center ${GLASS}`}>
            <div className="text-3xl">🪙</div>
            <div className="mt-2 text-[15px]">Заявок пока нет</div>
            <div className="mt-1 text-[13px] text-white/45">Пополните счёт — заявка появится здесь</div>
          </div>
        ) : sortedVisible.length === 0 ? (
          <div className="rounded-2xl border border-white/[0.06] bg-surface-card px-4 py-6 text-center text-[14px] text-white/45">
            Активных заявок нет
          </div>
        ) : (
          <ul className="space-y-2">
            {sortedVisible.map((o) => (
              <OrderCard
                key={o.id}
                order={o}
                onOpen={() => onOpenOrder(o.id)}
                onHide={TERMINAL.includes(o.status) ? () => onHideOrder?.(o.id) : undefined}
              />
            ))}
          </ul>
        )}

        {hiddenOrders.length > 0 && (
          <div className="mt-4">
            <button
              onClick={() => setHistOpen((v) => !v)}
              className="flex w-full items-center justify-between py-2 text-[14px] text-white/55 active:opacity-70"
            >
              <span>История</span>
              <span className="text-white/40">
                {hiddenOrders.length} {histOpen ? "▲" : "▼"}
              </span>
            </button>
            {histOpen && (
              <ul className="mt-1 space-y-2">
                {hiddenOrders.map((o) => (
                  <OrderCard key={o.id} order={o} onOpen={() => onOpenOrder(o.id)} onUnhide={() => onUnhideOrder?.(o.id)} muted />
                ))}
              </ul>
            )}
          </div>
        )}

        {withdrawals.length > 0 && (
          <div className="mt-6">
            <div className="mb-3 flex items-center justify-between">
              <h2 className="text-[17px] font-semibold">Выводы</h2>
              <span className="text-[13px] text-white/45">{withdrawals.length}</span>
            </div>
            <ul className="space-y-2">
              {withdrawals.map((w) => (
                <WithdrawalCard key={w.id} w={w} onOpen={() => onOpenWithdrawal?.(w)} />
              ))}
            </ul>
          </div>
        )}

        {resetLabel && (
          <button onClick={onReset} className="mt-8 block w-full text-center text-[13px] text-white/45">
            {resetLabel}
          </button>
        )}
      </div>

      {topupOpen && (
        <AssetSheet
          title="Что пополнить?"
          subtitle="Выберите актив для пополнения счёта."
          usdtSubtitle="Перевод USDT на свой кошелёк"
          onClose={() => setTopupOpen(false)}
          onRub={() => {
            setTopupOpen(false);
            onBuy();
          }}
          onUsdt={() => {
            setTopupOpen(false);
            onTopUpUsdt?.();
          }}
        />
      )}

      {withdrawOpen && (
        <AssetSheet
          title="Что вывести?"
          subtitle="Выберите актив для вывода."
          usdtSubtitle="Вывод USDT на внешний кошелёк"
          onClose={() => setWithdrawOpen(false)}
          onRub={() => {
            setWithdrawOpen(false);
            onWithdraw?.();
          }}
          onUsdt={() => {
            setWithdrawOpen(false);
            onWithdrawUsdt?.();
          }}
        />
      )}
    </div>
  );
}

// Лист выбора актива (пополнение/вывод): bottom-sheet на телефоне, карточка по центру на широком.
function AssetSheet({
  title,
  subtitle,
  usdtSubtitle,
  onClose,
  onRub,
  onUsdt,
}: {
  title: string;
  subtitle: string;
  usdtSubtitle: string;
  onClose: () => void;
  onRub: () => void;
  onUsdt: () => void;
}) {
  return (
    <div className="fixed inset-0 z-50 flex items-end justify-center sm:items-center" role="dialog" aria-modal="true">
      <div className="absolute inset-0 bg-black/60" onClick={onClose} />
      <div className="relative z-10 w-full rounded-t-3xl border border-white/[0.06] bg-surface-card p-5 pb-[calc(1.25rem+env(safe-area-inset-bottom))] text-white sm:max-w-sm sm:rounded-3xl">
        <h2 className="text-[18px] font-semibold">{title}</h2>
        <p className="mt-1 text-[14px] text-white/55">{subtitle}</p>
        <div className="mt-4 space-y-2.5">
          <TopUpOption badge="₽" badgeClass="from-brand-400 to-brand-600" title="Рубли" subtitle="Рублёвый счёт" onClick={onRub} />
          <TopUpOption badge="₮" badgeClass="from-teal-300 to-teal-500" title="USDT" subtitle={usdtSubtitle} onClick={onUsdt} />
        </div>
        <button onClick={onClose} className="mt-4 w-full rounded-2xl border border-white/10 px-4 py-3 text-[15px] active:bg-white/5">
          Отмена
        </button>
      </div>
    </div>
  );
}

function TopUpOption({
  badge,
  badgeClass,
  title,
  subtitle,
  onClick,
}: {
  badge: string;
  badgeClass: string;
  title: string;
  subtitle: string;
  onClick: () => void;
}) {
  return (
    <button
      onClick={onClick}
      className="flex w-full items-center gap-3 rounded-2xl bg-white/[0.04] px-3.5 py-3 text-left ring-1 ring-inset ring-white/[0.05] active:bg-white/[0.08]"
    >
      <div
        className={`grid h-10 w-10 shrink-0 place-items-center rounded-xl bg-gradient-to-br text-[17px] font-bold text-surface-base shadow-md shadow-black/20 ${badgeClass}`}
      >
        {badge}
      </div>
      <div className="min-w-0 flex-1">
        <div className="text-[15px] font-semibold leading-tight">{title}</div>
        <div className="mt-0.5 text-[12px] text-white/45">{subtitle}</div>
      </div>
      <span className="text-white/30">›</span>
    </button>
  );
}

function OrderCard({
  order,
  onOpen,
  onHide,
  onUnhide,
  muted,
}: {
  order: ApiOrder;
  onOpen: () => void;
  onHide?: () => void;
  onUnhide?: () => void;
  muted?: boolean;
}) {
  const s = STATUS_LABEL[order.status];
  const [dx, setDx] = useState(0);
  const startX = useRef<number | null>(null);
  const moved = useRef(false);
  const swipeable = !!onHide;

  const onTouchStart = (e: TouchEvent) => {
    if (!swipeable) return;
    startX.current = e.touches[0].clientX;
    moved.current = false;
  };
  const onTouchMove = (e: TouchEvent) => {
    if (startX.current == null) return;
    const d = e.touches[0].clientX - startX.current;
    if (Math.abs(d) > 8) moved.current = true;
    if (d < 0) setDx(Math.max(d, -96));
  };
  const onTouchEnd = () => {
    if (startX.current == null) return;
    if (dx < -60 && onHide) onHide();
    setDx(0);
    startX.current = null;
  };
  const handleOpen = () => {
    if (moved.current) {
      moved.current = false;
      return;
    }
    onOpen();
  };

  return (
    <li className="relative overflow-hidden rounded-2xl">
      <div
        onClick={handleOpen}
        onTouchStart={onTouchStart}
        onTouchMove={onTouchMove}
        onTouchEnd={onTouchEnd}
        style={swipeable ? { transform: `translateX(${dx}px)` } : undefined}
        className={`relative flex cursor-pointer items-center justify-between gap-3 rounded-2xl border border-white/[0.06] px-4 py-3.5 active:opacity-70 ${
          muted ? "bg-white/[0.02]" : "bg-white/[0.03]"
        }`}
      >
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2">
            <span className="text-[15px] font-medium">{formatRub(order.amount_rub)}</span>
            <Badge tone={s.tone} size="sm">
              {s.label}
            </Badge>
          </div>
          <div className="mt-1 truncate text-[12px] text-white/45">
            Пополнение · {formatRelative(Date.parse(order.created_at))}
          </div>
        </div>
        {onHide ? (
          <button
            onClick={(e) => {
              e.stopPropagation();
              onHide();
            }}
            className="grid h-7 w-7 shrink-0 place-items-center rounded-full text-[15px] text-white/40 active:bg-white/10"
            aria-label="Скрыть"
          >
            ✕
          </button>
        ) : onUnhide ? (
          <button
            onClick={(e) => {
              e.stopPropagation();
              onUnhide();
            }}
            className="shrink-0 text-[13px] font-medium text-brand active:opacity-70"
          >
            Вернуть
          </button>
        ) : (
          <span className="shrink-0 text-white/30">›</span>
        )}
      </div>
    </li>
  );
}

function WithdrawalCard({ w, onOpen }: { w: ApiWithdrawal; onOpen: () => void }) {
  const s = WITHDRAWAL_STATUS[w.status];
  return (
    <li
      onClick={onOpen}
      className="relative flex cursor-pointer items-center justify-between gap-3 rounded-2xl border border-white/[0.06] bg-white/[0.03] px-4 py-3.5 active:opacity-70"
    >
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2">
          <span className="text-[15px] font-medium">{formatUsdt(w.net_usdt)}</span>
          <Badge tone={s.tone} size="sm">
            {s.label}
          </Badge>
        </div>
        <div className="mt-1 truncate text-[12px] text-white/45">
          {WD_METHOD_LABEL[w.method]} · {wdDestSummary(w)} · {formatRelative(Date.parse(w.created_at))}
        </div>
      </div>
      <span className="shrink-0 text-white/30">›</span>
    </li>
  );
}

function ContactRow({
  icon,
  label,
  value,
}: {
  icon: ReactNode;
  label: string;
  value?: string;
}) {
  return (
    <div className="flex w-full items-center gap-3 px-4 py-3">
      <span className="grid h-8 w-8 shrink-0 place-items-center rounded-lg bg-white/[0.06] text-[15px]">{icon}</span>
      <span className="min-w-0 flex-1">
        <span className="block text-[12px] text-white/45">{label}</span>
        <span className="block truncate text-[14px] text-white/80">{value || "—"}</span>
      </span>
    </div>
  );
}

export function resetDemo(): void {
  clearAll();
}
